import React, { useState, useCallback, useRef, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import BiCalculator from './BiCalculator'

const DEFAULT_SECTIONS = ['Workshop Preparation', 'Onsite', 'Finish']

export default function LabourSection({
  project, labourLines, timeTasks, taskCatalogue, onTaskCatalogueSave,
  onLabourLinesChange, onTimeTasksChange, onProjectUpdate, userId, inline,
  hoursPerDay = 8,
}) {
  const [isOpen, setIsOpen] = useState(true)
  const effectiveIsOpen = inline ? true : isOpen
  const [calcOpen, setCalcOpen] = useState(false)
  const [addQuery, setAddQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [sectionOrder, setSectionOrder] = useState(null)
  const [showLabourMyItems, setShowLabourMyItems] = useState(false)
  const labourMyItemsRef = useRef(null)
  useEffect(() => { if (showLabourMyItems) labourMyItemsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }) }, [showLabourMyItems])
  const [labourCatalogueItems, setLabourCatalogueItems] = useState([])

  // Derive sections from tasks, preserving order
  // Derive sections: always start from DEFAULT_SECTIONS, then merge task sections + user reordering
  const sections = (() => {
    // Get all section names currently in tasks
    const fromTasks = []
    const seen = new Set()
    timeTasks.forEach(t => {
      const s = t.section || 'General'
      if (!seen.has(s)) { seen.add(s); fromTasks.push(s) }
    })
    // Base: sectionOrder if user has reordered, otherwise DEFAULT_SECTIONS
    const base = sectionOrder || DEFAULT_SECTIONS
    // Merge: start from base, add any task sections not already present
    const result = [...base]
    fromTasks.forEach(s => { if (!result.includes(s)) result.push(s) })
    return result
  })()

  const biDays = useCallback(() =>
    timeTasks.reduce((sum, t) => sum + (parseFloat(t.unit_duration) || 0), 0) / hoursPerDay
  , [timeTasks, hoursPerDay])

  const labourSubtotal = useCallback(() => {
    const manualTotal = labourLines.reduce((sum, line) => {
      const days = line.from_estimator ? biDays() : (line.days || 0)
      return sum + days * (project?.day_rate || 350)
    }, 0)
    // If there are B&I tasks but no from_estimator labour line, still count them in total
    const hasFromEstimator = labourLines.some(l => l.from_estimator)
    const orphanBiDays = !hasFromEstimator && timeTasks.length > 0 ? biDays() : 0
    return manualTotal + orphanBiDays * (project?.day_rate || 350)
  }, [labourLines, biDays, timeTasks, project])

  const margin = useCallback(() =>
    labourSubtotal() * ((project?.labour_markup_pct || 10) / 100)
  , [labourSubtotal, project])

  const total = () => labourSubtotal() + margin()

  // ── My Items fetch ────────────────────────────────────────────
  const fetchLabourMyItems = async () => {
    if (!userId) return
    const { data } = await supabase.from('item_catalogue')
      .select('*').eq('user_id', userId).eq('category', 'labour').eq('is_active', true).order('sort_order')
    const existingNames = new Set(labourLines.map(l => l.name?.toLowerCase().trim()))
    setLabourCatalogueItems((data || []).filter(i => !existingNames.has(i.description?.toLowerCase().trim())))
    setShowLabourMyItems(true)
  }

  const addLabourFromMyItems = async (catItem) => {
    if (!project?.id) return
    const { data, error } = await supabase.from('labour_lines').insert({
      project_id: project.id, name: catItem.description,
      days: 0, from_estimator: false, sort_order: labourLines.length,
    }).select().single()
    if (!error && data) {
      onLabourLinesChange([...labourLines, data])
      setLabourCatalogueItems(prev => prev.filter(i => i.id !== catItem.id))
    }
  }

  const addBiTaskFromMyItems = async (catItem, targetSection) => {
    if (!project?.id) return
    const name = catItem.name || catItem.description
    const { data, error } = await supabase.from('time_tasks').insert({
      project_id: project.id, description: name,
      section: targetSection,
      phase: catItem.phase || targetSection,
      is_multiplied: catItem.is_multiplied || false,
      unit_duration: catItem.unit_duration || 0,
      quantity: 1, sort_order: timeTasks.length,
    }).select().single()
    if (!error && data) {
      onTimeTasksChange([...timeTasks, data])
      setBiCatalogueItems(prev => prev.filter(i => i.id !== catItem.id))
    }
  }

  const addSectionAtBottom = async (name) => {
    if (!name || !name.trim()) return
    if (!project?.id) { console.error('addSectionAtBottom: no project.id', project); return }
    const { data, error } = await supabase.from('time_tasks').insert({
      project_id: project.id, description: 'New task',
      section: name.trim(), phase: 'Workshop',
      is_multiplied: false, unit_duration: 0, quantity: 1,
      sort_order: timeTasks.length,
    }).select().single()
    if (error) { console.error('addSectionAtBottom error:', error); return }
    if (data) {
      onTimeTasksChange([...timeTasks, data])
      setSectionOrder(prev => {
        const base = prev || sections
        return base.includes(name.trim()) ? base : [...base, name.trim()]
      })
    }
  }

  // ── Labour line ops ────────────────────────────────────────────

  const updateLine = async (lineId, field, value) => {
    const parsed = field === 'days' ? (parseFloat(value) || 0) : value
    onLabourLinesChange(labourLines.map(l => l.id === lineId ? { ...l, [field]: parsed } : l))
    await supabase.from('labour_lines').update({ [field]: parsed }).eq('id', lineId)
  }

  const deleteLine = async (lineId) => {
    onLabourLinesChange(labourLines.filter(l => l.id !== lineId))
    await supabase.from('labour_lines').delete().eq('id', lineId)
  }

  const addLabourLine = async (name = '', afterId = null) => {
    if (!project?.id) return
    const idx = afterId ? labourLines.findIndex(l => l.id === afterId) : labourLines.length - 1
    const { data, error } = await supabase.from('labour_lines').insert({
      project_id: project?.id, name: name || 'New line',
      days: 0, from_estimator: false, sort_order: idx + 1,
    }).select().single()
    if (!error && data) {
      const newLines = [...labourLines]
      newLines.splice(idx + 1, 0, data)
      onLabourLinesChange(newLines)
    }
    setAddQuery(''); setShowDropdown(false)
  }

  // ── Task ops ──────────────────────────────────────────────────

  const syncBiLine = async (tasks) => {
    const totalDays = tasks.reduce((sum, t) => sum + (parseFloat(t.unit_duration) || 0), 0) / hoursPerDay
    const biLine = labourLines.find(l => l.from_estimator)
    if (biLine) {
      await supabase.from('labour_lines').update({ days: totalDays }).eq('id', biLine.id)
      onLabourLinesChange(labourLines.map(l => l.from_estimator ? { ...l, days: totalDays } : l))
    }
  }

  const updateTaskDays = async (taskId, value) => {
    const days = parseFloat(value) || 0
    const updated = timeTasks.map(t => t.id === taskId ? { ...t, unit_duration: days } : t)
    onTimeTasksChange(updated)
    await supabase.from('time_tasks').update({ unit_duration: days }).eq('id', taskId)
    syncBiLine(updated)
  }

  const updateTaskNotes = async (taskId, notes) => {
    onTimeTasksChange(timeTasks.map(t => t.id === taskId ? { ...t, notes } : t))
    const { error } = await supabase.from('time_tasks').update({ notes }).eq('id', taskId)
    if (error) console.error('updateTaskNotes:', error)
  }

  const updateTaskName = async (taskId, description) => {
    onTimeTasksChange(timeTasks.map(t => t.id === taskId ? { ...t, description } : t))
    await supabase.from('time_tasks').update({ description }).eq('id', taskId)
  }

  const removeTask = async (taskId) => {
    const updated = timeTasks.filter(t => t.id !== taskId)
    onTimeTasksChange(updated)
    await supabase.from('time_tasks').delete().eq('id', taskId)
    syncBiLine(updated)
  }

  const addTaskBelow = async (afterId = null, section = 'General') => {
    if (!project?.id) { console.error('addTaskBelow: no project.id', project); return }
    const idx = afterId ? timeTasks.findIndex(t => t.id === afterId) : timeTasks.length - 1
    const taskSection = afterId
      ? (timeTasks.find(t => t.id === afterId)?.section || section)
      : section
    const { data, error } = await supabase.from('time_tasks').insert({
      project_id: project.id, description: 'New task',
      section: taskSection, phase: 'Workshop',
      is_multiplied: false, unit_duration: 0, quantity: 1,
      sort_order: Math.max(0, idx + 1),
    }).select().single()
    if (error) { console.error('addTaskBelow error:', error); return }
    if (data) {
      const updated = [...timeTasks]
      updated.splice(Math.max(0, idx + 1), 0, data)
      onTimeTasksChange(updated)
      syncBiLine([...timeTasks, data])
    }
  }

  const renameSection = async (oldName, newName) => {
    if (!newName.trim() || newName === oldName) { setEditingSection(null); return }
    const updated = timeTasks.map(t =>
      (t.section || 'General') === oldName ? { ...t, section: newName } : t
    )
    onTimeTasksChange(updated)
    await Promise.all(
      timeTasks
        .filter(t => (t.section || 'General') === oldName)
        .map(t => supabase.from('time_tasks').update({ section: newName }).eq('id', t.id))
    )
    // Update sectionOrder to reflect new name
    setSectionOrder(prev => {
      const base = prev || sections
      return base.map(s => s === oldName ? newName : s)
    })
    setEditingSection(null)
  }

  const deleteSection = async (sectionName) => {
    const sectionTasks = timeTasks.filter(t => (t.section || 'General') === sectionName)
    const updated = timeTasks.filter(t => (t.section || 'General') !== sectionName)
    onTimeTasksChange(updated)
    await Promise.all(sectionTasks.map(t => supabase.from('time_tasks').delete().eq('id', t.id)))
    syncBiLine(updated)
  }

  // ── Save task to catalogue ───────────────────────────────────
  const [savedTaskIds, setSavedTaskIds] = useState(new Set())

  const saveTaskToCatalogue = async (task) => {
    if (!userId) return
    const name = task.description?.trim()
    if (!name) return
    // Already in catalogue?
    const existing = (taskCatalogue || []).find(t =>
      (t.name || '').toLowerCase() === name.toLowerCase()
    )
    if (existing) return
    // Map project section name to catalogue phase
    const sectionLower = (task.section || '').toLowerCase()
    let phase = 'Workshop'
    if (sectionLower.includes('onsite') || sectionLower.includes('install') || sectionLower.includes('site')) phase = 'Install'
    else if (sectionLower.includes('finish') || sectionLower.includes('prep') || sectionLower.includes('preparation')) phase = 'Prep'
    const { data } = await supabase.from('task_catalogue').insert({
      user_id: userId,
      name,
      phase,
      is_multiplied: task.is_multiplied || false,
      unit_duration: task.unit_duration || 0,
      sort_order: (taskCatalogue || []).length,
    }).select().single()
    if (data) {
      setSavedTaskIds(prev => new Set([...prev, task.id]))
      if (onTaskCatalogueSave) onTaskCatalogueSave(data)
    }
  }

  const isTaskSaved = (task) => {
    if (savedTaskIds.has(task.id)) return true
    return (taskCatalogue || []).some(t =>
      (t.name || '').toLowerCase() === (task.description || '').toLowerCase()
    )
  }

  // ── Helpers ───────────────────────────────────────────────────

  const labourMatches = () => {
    if (!addQuery.trim()) return []
    const q = addQuery.toLowerCase()
    const existing = new Set(labourLines.map(l => l.name.toLowerCase()))
    return (taskCatalogue || [])
      .filter(t => (t.name || '').toLowerCase().includes(q) && !existing.has((t.name || '').toLowerCase()))
      .slice(0, 4)
  }

  const dayRate = project?.day_rate || 350
  const marginPct = project?.labour_markup_pct || 10

  const biTasks = timeTasks.map(t => ({
    id: t.id,
    name: t.description || '',
    section: t.section || 'General',
    hrs: parseFloat(t.unit_duration) || 0,
    note: t.notes || '',
  }))

  return (
    <>
    <div className={inline ? '' : 'section'} style={inline ? {} : { '--cat': 'var(--c-adhesives)' }}>
      {!inline && (
        <div className="sec-head" onClick={() => setIsOpen(o => !o)}>
          <div className="sec-dot" />
          <span className="sec-title">Labour</span>
          <div className="sec-right">
            <span className="sec-total">£{total().toFixed(2)}</span>
            <span className={`chev${isOpen ? '' : ' collapsed'}`}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </span>
          </div>
        </div>
      )}

      {effectiveIsOpen && (
        <div className="sec-body" style={{ height: 'auto' }}>

          {/* Rate bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', padding: '0.55rem 1.25rem', background: 'color-mix(in srgb,var(--c-adhesives) 8%,var(--surface))', borderBottom: '1px solid var(--line)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="t-label" style={{ color: 'var(--c-adhesives)' }}>Day Rate</span>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--line-2)', borderRadius: 'var(--radius-ctl)', background: 'var(--surface)', overflow: 'hidden' }}>
                <span style={{ padding: '0 0.2rem 0 0.5rem', fontFamily: 'var(--mono)', fontSize: '0.78rem', color: 'var(--ink-3)' }}>£</span>
                <input
                  className="numcell"
                  style={{ width: '60px', background: 'transparent' }}
                  type="number"
                  value={project.day_rate}
                  onChange={e => onProjectUpdate('day_rate', e.target.value)}
                  onBlur={e => onProjectUpdate('day_rate', parseFloat(e.target.value) || 350, true)}
                />
              </div>
            </div>
          </div>

          {/* Column headers */}
          <div
            className="items-head"
            style={{ gridTemplateColumns: '22px minmax(0,1fr) 180px 100px 24px 28px' }}
          >
            <span className="col-h" />
            <span className="col-h">Line item</span>
            <span className="col-h c">Days / Hrs</span>
            <span className="col-h r">Amount</span>
            <span className="col-h" />
            <span className="col-h" />
          </div>

          {labourLines.map((line) => {
            const days = line.from_estimator ? biDays() : (line.days || 0)
            const amount = days * dayRate
            const wholeDays = Math.floor(days)
            const remHours = Math.round((days % 1) * hoursPerDay)
            return (
              <div
                key={line.id}
                className="item-row"
                style={{
                  gridTemplateColumns: '22px minmax(0,1fr) 180px 100px 24px 28px',
                  ...(line.from_estimator ? { background: 'var(--green-soft)' } : {}),
                  ...(days === 0 && !line.from_estimator ? { opacity: 0.45 } : {}),
                }}
              >
                <div className="drag" title="Drag to reorder">
                  <svg width="10" height="14" viewBox="0 0 10 14" fill="none"><circle cx="3" cy="3" r="1.2" fill="currentColor"/><circle cx="7" cy="3" r="1.2" fill="currentColor"/><circle cx="3" cy="7" r="1.2" fill="currentColor"/><circle cx="7" cy="7" r="1.2" fill="currentColor"/><circle cx="3" cy="11" r="1.2" fill="currentColor"/><circle cx="7" cy="11" r="1.2" fill="currentColor"/></svg>
                </div>
                <div className="item-name">
                  {line.from_estimator ? (
                    <span>
                      {line.name}
                      <span style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', color: 'var(--green)', marginLeft: '0.4rem' }}>← from calculator</span>
                    </span>
                  ) : (
                    <input
                      className="ak-edit"
                      style={{ padding: '0' }}
                      value={line.name}
                      onChange={e => onLabourLinesChange(labourLines.map(l => l.id === line.id ? { ...l, name: e.target.value } : l))}
                      onBlur={e => updateLine(line.id, 'name', e.target.value)}
                    />
                  )}
                </div>
                {line.from_estimator ? (
                  <div className="tnum" style={{ textAlign: 'center', fontFamily: 'var(--mono)', fontSize: '0.82rem', color: 'var(--green)', fontWeight: 500 }}>
                    {wholeDays}d {remHours}h
                  </div>
                ) : (
                  <TimeInput
                    hours={line.days * hoursPerDay}
                    hoursPerDay={hoursPerDay}
                    onChange={h => updateLine(line.id, 'days', h / hoursPerDay)}
                  />
                )}
                <div className="total-cell">{amount > 0 ? `£${amount.toFixed(2)}` : '—'}</div>
                <button
                  className="act"
                  title="Add line below"
                  onClick={() => addLabourLine('', line.id)}
                  style={{ fontSize: '1rem', color: 'var(--ink-3)' }}
                >+</button>
                <div className="actions" style={{ opacity: 1 }}>
                  <button className="act del" onClick={() => deleteLine(line.id)}>×</button>
                </div>
              </div>
            )
          })}

          {!labourLines.some(l => l.from_estimator) && biDays() > 0 && (
            <div
              className="item-row"
              style={{ gridTemplateColumns: '22px minmax(0,1fr) 180px 100px 24px 28px', background: 'var(--green-soft)' }}
            >
              <div />
              <div className="item-name">
                <span>
                  Build &amp; Install
                  <span style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', color: 'var(--green)', marginLeft: '0.4rem' }}>← from calculator</span>
                </span>
              </div>
              <div className="tnum" style={{ textAlign: 'center', fontFamily: 'var(--mono)', fontSize: '0.82rem', color: 'var(--green)', fontWeight: 500 }}>
                {Math.floor(biDays())}d {Math.round((biDays() % 1) * hoursPerDay)}h
              </div>
              <div className="total-cell">£{(biDays() * dayRate).toFixed(2)}</div>
              <div /><div />
            </div>
          )}

          {/* Add bar */}
          <div className="addbar">
            <button className="add-btn" onClick={() => addLabourLine()}>+ Add item</button>
            <button className="add-btn" onClick={fetchLabourMyItems}>+ Add from My Items</button>
            <button className="add-btn" onClick={() => setCalcOpen(o => !o)}>⏱ Build &amp; Install Calculator</button>
          </div>

          {/* Labour My Items panel */}
          {showLabourMyItems && (
            <div ref={labourMyItemsRef} className="ak-card" style={{ margin: '0 18px 12px', overflow: 'visible' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.85rem', borderBottom: '1px solid var(--line)', background: 'var(--surface-2)' }}>
                <span className="t-label">My Items — Labour</span>
                <button className="ak-iconbtn" onClick={() => setShowLabourMyItems(false)} style={{ width: 'auto', padding: '2px 8px', fontSize: '0.72rem', fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>× Close</button>
              </div>
              <div style={{ padding: '0.25rem 0', maxHeight: '360px', overflowY: 'auto' }}>
                {labourCatalogueItems.length === 0
                  ? <div style={{ padding: '0.5rem 0.85rem', fontFamily: 'var(--mono)', fontSize: '0.65rem', color: 'var(--ink-3)' }}>No labour items in My Items</div>
                  : labourCatalogueItems.map(item => (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.45rem 0.85rem', borderBottom: '1px solid var(--line)' }}>
                      <span style={{ fontSize: '0.82rem', flex: 1, color: 'var(--ink)' }}>{item.description}</span>
                      <button className="add-btn ak-btn--sm" onClick={() => addLabourFromMyItems(item)}>+ Add</button>
                    </div>
                  ))
                }
              </div>
            </div>
          )}

        </div>
      )}
    </div>
    {calcOpen && (
      <BiCalculator
        context="project"
        sections={sections}
        tasks={biTasks}
        hoursPerDay={hoursPerDay}
        dayRate={dayRate}
        subtitle={project?.name}
        taskCatalogue={taskCatalogue}
        onTaskName={(id, name, persist) => {
          onTimeTasksChange(timeTasks.map(t => t.id === id ? { ...t, description: name } : t))
          if (persist) supabase.from('time_tasks').update({ description: name }).eq('id', id).then()
        }}
        onTaskHrs={(id, hrs) => {
          const updated = timeTasks.map(t => t.id === id ? { ...t, unit_duration: hrs } : t)
          onTimeTasksChange(updated)
          supabase.from('time_tasks').update({ unit_duration: hrs }).eq('id', id).then()
          syncBiLine(updated)
        }}
        onTaskNote={(id, note, persist) => {
          onTimeTasksChange(timeTasks.map(t => t.id === id ? { ...t, notes: note } : t))
          if (persist) supabase.from('time_tasks').update({ notes: note }).eq('id', id).then()
        }}
        onTaskDelete={removeTask}
        onTaskAdd={(section) => addTaskBelow(null, section)}
        onReorderTasks={async (reordered) => {
          const updated = reordered.map(nt => {
            const orig = timeTasks.find(t => t.id === nt.id)
            return orig ? { ...orig, section: nt.section } : null
          }).filter(Boolean)
          onTimeTasksChange(updated)
          await Promise.all(updated.map((t, i) =>
            supabase.from('time_tasks').update({ sort_order: i, section: t.section }).eq('id', t.id)
          ))
        }}
        onSectionAdd={addSectionAtBottom}
        onSectionDelete={deleteSection}
        onSectionRename={renameSection}
        onReorderSections={setSectionOrder}
        onAddFromCatalogue={addBiTaskFromMyItems}
        onSaveToLibrary={(task) => {
          const origTask = timeTasks.find(t => t.id === task.id)
          if (origTask) saveTaskToCatalogue(origTask)
        }}
        onDayRateChange={(val, persist) => onProjectUpdate && onProjectUpdate('day_rate', val, persist)}
        onHoursPerDayChange={(val, persist) => onProjectUpdate && onProjectUpdate('hours_per_day', val, persist)}
        onClose={() => setCalcOpen(false)}
      />
    )}
    </>
  )
}

function TimeInput({ hours, hoursPerDay, onChange }) {
  const d = Math.floor(hours / hoursPerDay)
  const h = Math.round(hours % hoursPerDay)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, justifyContent: 'center' }}>
      <button
        className="bi-time-btn"
        onClick={() => onChange(Math.max(0, hours - 1))}
      >−</button>
      <input
        className="bi-time-inp"
        style={{ width: d >= 10 ? '3ch' : '2ch' }}
        type="number"
        value={d}
        onFocus={e => e.target.select()}
        onChange={e => onChange(Math.max(0, parseInt(e.target.value, 10) || 0) * hoursPerDay + h)}
      />
      <sup className="bi-time-sup">d</sup>
      <span className="bi-time-sep">·</span>
      <input
        className="bi-time-inp"
        style={{ width: '2ch' }}
        type="number"
        value={h}
        onFocus={e => e.target.select()}
        onChange={e => onChange(d * hoursPerDay + Math.min(hoursPerDay - 1, Math.max(0, parseInt(e.target.value, 10) || 0)))}
      />
      <sup className="bi-time-sup">h</sup>
      <button
        className="bi-time-btn"
        onClick={() => onChange(hours + 1)}
      >+</button>
    </div>
  )
}
